
import { Enums } from '@bode-canada/core';
import { StyleSizeType, StyleColorSchemeType } from '@/ui-kit/styles/enums';
import { ToastMessage } from '@/ui-kit/components/toast/enums';
import { TooltipMessage } from '@/ui-kit/components/tooltip/enum';
import ModalService from '@/ui-kit/components/modal/ModalService';
import StyleClassNamesBuilder from '@/ui-kit/styles/helpers/StyleClassNamesBuilder';
import ListingsApiService from '@/services/api/listings';
import DashboardApiService from '@/services/api/serverless/dashboard';
import EventsApiService from '@/services/api/events';
import NewListingSoldApiService from '@/components/features/sold-property/api/NewListingSoldApiService';
import routes, { VIEWINGS } from '@/constants/routes';
import { ContainerType, SectionFillType } from '@/components/page-specific/listing/section/enums';
import ListingSection from '@/components/page-specific/listing/section';
import ListingPreviewAccordion from '@/components/page-specific/listing/preview/accordion';
import ListingSummary from '@/components/page-specific/listing/summary';
import ListingTransactionTip from '@/components/page-specific/listing/transaction-tip';
import ListingBodeLabel from '@/components/page-specific/listing/bode-label';
import ListingLicence from '@/components/page-specific/listing/licence';
import ListingRAEDisclaimer from '@/components/page-specific/listing/rae-disclaimer';
import ListingGallery from '@/components/page-specific/listing/gallery';
import ListingMap from '@/components/page-specific/listing/map';
import ListingPropertyOverview from '@/components/page-specific/listing/property-overview';
import { PropertyOverviewTitle } from '@/components/page-specific/listing/property-overview/enums';
import ListingEstimatedFees from '@/components/page-specific/listing/estimated-fees';
import ListingAccordion from '@/components/page-specific/listing/accordion';
import ListingAccordionHelper from '@/components/page-specific/listing/accordion/helpers';
import ListingComparable from '@/components/shared/comparable/ListingComparable';
import ListingOwnerContacts from '@/components/page-specific/listing/owner-contacts';
import { ListingGallerySlotName } from '@/components/page-specific/listing/gallery/enums';
import ListingOverviewHelper from '@/components/page-specific/listing/property-overview/helpers';
import { ADD_ROLE, AUTHENTICATE, getAction } from '@/store/user/actions';
import ToastService from '@/components/toast/toast-service';
import VerificationModal from '@/components/verification/modal';
import { LISTING_TRACK_EVENT_ORIGIN_TYPES } from '@/constants';
import { page } from '@/utils/analytics';
import ListingHeadMixin from '@/components/page-specific/listing/mixins/listing-head';
import NavigationLinksMixin from '@/components/page-specific/listing/mixins/navigation-links';
import PropertyTransaction from '@/components/features/sold-property/models/PropertyTransaction';
import LoadListingService, { ListingMapper as ListingDtoMapper } from '@/components/page-specific/listing/services/LoadListingService';
import QueryBuilderFactory from '@/components/features/comparables/factories/queryBuilderFactory';
import ComparableApiService from '@/services/api/serverless/comparable/ComparableApiService';
import ListingMapper from '@/components/features/comparables/shared/mappers/Listing';
import ZohoListingDeskTicketHelper from '@/helpers/zoho-listing-desk-ticket';
import { ZohoListingDeskTicketType } from '@/enums/zoho';
import BusinessAccountHelper from '@/utils/user/BusinessAccountHelper';
import useCanonicalHead from '@/mixins/useCanonicalHead';
import { FiltersCreateHelper } from '@/components/features/comparables/helpers/filtersCreateHelper';

const MIN_COMPARABLE_MATCH_VALUE = 60;
const { ListingStatusEnum } = Enums;

export default {
  name: 'ListingPage',
  components: {
    ListingSection,
    ListingTransactionTip,
    ListingSummary,
    ListingGallery,
    ListingBodeLabel,
    ListingLicence,
    ListingRAEDisclaimer,
    ListingMap,
    ListingPropertyOverview,
    ListingEstimatedFees,
    ListingAccordion,
    ListingComparable,
    ListingOwnerContacts,
    ListingPreviewAccordion,
  },
  mixins: [ListingHeadMixin, NavigationLinksMixin, useCanonicalHead()],
  asyncData({ payload }) {
    return payload ? { listingDto: payload, isLoaded: true } : {};
  },
  data() {
    return {
      soldDataDto: [],
      comparableListings: [],
      isLoaded: false,
      ListingGallerySlotName,
      StyleSizeType,
      StyleColorSchemeType,
      ContainerType,
      SectionFillType,
      TooltipMessage,
    };
  },
  computed: {
    isPreviewMode() {
      return [
        Enums.ListingStatusEnum.EXPIRED,
        Enums.ListingStatusEnum.TERMINATED,
        Enums.ListingStatusEnum.HIDDEN,
        Enums.ListingStatusEnum.DRAFT,
      ].includes(this.listing.status);
    },
    listingPagesClassNames() {
      return new StyleClassNamesBuilder('listing-page')
        .addConditionClassName('preview-page', this.isPreviewMode)
        .build();
    },
    user() {
      return this.$store.state.user.userData;
    },
    isUserAdmin() {
      return this.user.roles?.admin;
    },
    isUserAuthenticated() {
      return this.user.isAuthenticated;
    },
    isUserTitleHolder() {
      return this.listing.getIsUserTitleHolder(this.user.userId);
    },
    isUserBusinessPartner() {
      return BusinessAccountHelper.getIsUserBusinessPartner(
        this.user.userBusinessAccounts,
        BusinessAccountHelper.getUsersBusinessAccounts(this.listing.owners),
      );
    },
    isUserOwner() {
      return this.listing.getIsUserOwner(this.user.userId);
    },
    canUserEditListing() {
      return this.isUserOwner || this.isUserAdmin || this.isUserBusinessPartner;
    },
    editable() {
      return this.canUserEditListing && this.listing.status !== ListingStatusEnum.PENDING;
    },
    isDraftStatus() {
      return this.listing.status === ListingStatusEnum.DRAFT;
    },
    isVacantLandListing() {
      return this.listing.buildingType === Enums.PropertyTypeEnum.VACANT_LAND
        || this.listing.buildingClass === Enums.PropertyTypeEnum.VACANT_LAND;
    },
    totalBeds() {
      return this.isVacantLandListing
        ? null
        : this.listing.totalBeds;
    },
    totalBaths() {
      return this.isVacantLandListing
        ? null
        : this.listing.totalBaths;
    },
    finishedSqft() {
      return this.isVacantLandListing
        ? null
        : this.listing.finishedSqft;
    },
    excludedVacantLandProperties() {
      return [
        PropertyOverviewTitle.GarageSize,
        PropertyOverviewTitle.Beds,
        PropertyOverviewTitle.Heating,
        PropertyOverviewTitle.FullBaths,
        PropertyOverviewTitle.Cooling,
        PropertyOverviewTitle.HalfBaths,
        PropertyOverviewTitle.ParkingSpace,
        PropertyOverviewTitle.YearBuilt,
        PropertyOverviewTitle.Style,
      ];
    },
    requiresIdVerificationForShowings() {
      return this.listing.requiresIdVerificationForShowings;
    },
    accordionsConfiguration() {
      return ListingAccordionHelper.getFullListingAccordions(this.listing);
    },
    propertyOverviewCharacteristics() {
      return ListingOverviewHelper.getListingPropertyOverviewCharacteristics(
        this.listing,
        this.isVacantLandListing ? this.excludedVacantLandProperties : undefined,
      );
    },
    comparableRequestQueries() {
      return FiltersCreateHelper.getComparableRequestQueries(this.listing);
    },
    soldData() {
      const transaction = this.soldDataDto
        .find(({ status }) => (status === ListingStatusEnum.SOLD));

      return transaction && new PropertyTransaction(transaction, !this.user.isAuthenticated);
    },
  },
  async mounted() {
    await this.fetchListing();
    if (!this.listingDto) {
      await this.$router.push(routes.notFound);
      return;
    }
    this.trackPageLoad();
    await Promise.all([
      this.fetchSoldData(),
      this.fetchComparableListings(),
    ]);
  },

  methods: {
    async fetchListing() {
      const listingId = this.$route.params.id;
      const listingDto = await LoadListingService.loadListing(listingId);

      if (listingDto
        && !this.getIsNotAllowedToAccessListing(listingDto.listing, listingDto.users)
      ) {
        this.listingDto = listingDto;
        this.isLoaded = true;
        await this.incrementListingViews();
      }
    },

    async fetchComparableListings() {
      const listingFilters = FiltersCreateHelper.getListingComparableFilters(this.listing);
      const query = QueryBuilderFactory.getFields(this.comparableRequestQueries, listingFilters);
      const { active = [] } = await ComparableApiService.getComparable(query).catch(() => ({ active: [] }));

      this.comparableListings = active
        .map((listing) => ListingMapper.mapFromDto(listing))
        .filter((listing) => (listing.match >= MIN_COMPARABLE_MATCH_VALUE));
    },
    async fetchSoldData() {
      this.soldDataDto = await NewListingSoldApiService
        .getSoldTransactionsByAddressId(this.listing.addressId);
    },

    async incrementListingViews() {
      if (!this.isUserTitleHolder) {
        await DashboardApiService
          .createListingViewsByListingId(this.listing.listingId);
      }
    },

    trackPageLoad() {
      page({
        listingType: this.listing.origin,
        title: this.pageTitle,
      });
    },

    getIsNotAllowedToAccessListing({ status }, users) {
      const isOwner = users.some((user) => user.userId === this.user.userId);
      const isPartner = BusinessAccountHelper.getIsUserBusinessPartner(
        this.user.userBusinessAccounts,
        BusinessAccountHelper.getUsersBusinessAccounts(users),
      );
      const isAdmin = this.isUserAdmin;
      const isDraft = status === ListingStatusEnum.DRAFT;
      const ownerAllowedStatuses = [
        ListingStatusEnum.HIDDEN,
        ListingStatusEnum.DELETED,
        ListingStatusEnum.EXPIRED,
        ListingStatusEnum.TERMINATED,
        ListingStatusEnum.SUBMITTED,
        ListingStatusEnum.SOLD,
      ];

      return (ownerAllowedStatuses.includes(status) && !(isOwner || isAdmin || isPartner))
        || (isDraft && !this.isUserAuthenticated);
    },

    async handleRequestInfo(requestConfig) {
      await ListingsApiService.requestListingInfo(this.listing.listingId, requestConfig);
      ToastService.openPositive('Your request has been sent');
    },

    handleSelectUserRole(role) {
      if (this.user.isAuthenticated) {
        this.$store.dispatch(getAction(ADD_ROLE), role.toLowerCase());
      }
    },

    async handleMakeOffer() {
      await ZohoListingDeskTicketHelper.formatAndSendZohoTicket(
        ListingDtoMapper.mapToNewDto(this.listing),
        ZohoListingDeskTicketType.ListingOfferRequested,
      );

      return ToastService.openPositive(ToastMessage.OfferRequested);
    },

    async handleBookViewing(request) {
      if (!this.user.isAuthenticated) {
        await this.$store.dispatch(getAction(AUTHENTICATE));
      }
      const hasPermission = !this.listing.isBode
        || !this.listing.requiresIdVerificationForShowings
        || await this.getIsUserVerified();

      if (hasPermission) {
        const { eventId, agentData } = await EventsApiService.bookViewing({
          ...request,
          realtorId: this.listing.owners[0]?.userId,
          listingOrigin: this.listing.origin,
          listingId: this.listing.listingId,
        });
        const [mainTimeSlot, ...additionalTimes] = request.appointments;
        const listingType = this.listing.isBode
          ? LISTING_TRACK_EVENT_ORIGIN_TYPES.Bode
          : LISTING_TRACK_EVENT_ORIGIN_TYPES.MLS;

        await this.$router.push({
          path: `${routes.listings}/${this.listing.listingId}${VIEWINGS}/${listingType}`,
          query: {
            eventId,
            address: this.listing.fullAddress,
            startTime: mainTimeSlot.startTime,
            endTime: mainTimeSlot.endTime,
            additionalTimes,
            ...agentData,
          },
        });
      }
    },
    async getIsUserVerified() {
      return this.user.isVerified || ModalService.open({
        component: VerificationModal,
        data: {
          message: 'Your identity has to be verified in order to book a viewing on this property.',
          user: this.user,
        },
      });
    },
  },
};
